import '.c0{position:absolute;}.ssr-plugin'
import '.jc{right:0.25rem;}.ssr-plugin'
import '.jd{bottom:0.25rem;}.ssr-plugin'
import '.cv{z-index:2;}.ssr-plugin'
import '.d{display:flex;}.ssr-plugin'
import '.a7{flex-direction:row;}.ssr-plugin'
import '.ba{flex-wrap:wrap;}.ssr-plugin'
import '.u{align-items:center;}.ssr-plugin'
import '.bm{justify-content:flex-end;}.ssr-plugin'
import '.je{gap:0.125rem;}.ssr-plugin'
import '.ao{max-width:100%;}.ssr-plugin'
import '.jf{pointer-events:auto;}.ssr-plugin'
import { useTranslations } from '@i18n/useLanguage'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { $insertList, $removeList, $isListNode } from '@lexical/list'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  faBold,
  faItalic,
  faListOl,
  faListUl,
  faStrikethrough,
} from '@fortawesome/pro-regular-svg-icons'
import {
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
  $getSelection,
  $isRangeSelection,
  type TextFormatType,
} from 'lexical'
import { mergeRegister } from '@lexical/utils'
import { type MouseEvent, useCallback, useEffect, useState } from 'react'
import { type Style, styled } from 'inlines'

export type ToolbarFormatState = {
  bold: boolean
  italic: boolean
  strikethrough: boolean
  bulletList: boolean
  numberList: boolean
}

const defaultFormatState: ToolbarFormatState = {
  bold: false,
  italic: false,
  strikethrough: false,
  bulletList: false,
  numberList: false,
}

const readToolbarFormatState = (): ToolbarFormatState => {
  const selection = $getSelection()
  if (!$isRangeSelection(selection)) {
    return defaultFormatState
  }

  let bulletList = false
  let numberList = false
  let node = selection.anchor.getNode()
  while (node) {
    if ($isListNode(node)) {
      const listType = node.getListType()
      bulletList = listType === 'bullet'
      numberList = listType === 'number'
      break
    }
    const parent = node.getParent()
    if (!parent) {
      break
    }
    node = parent
  }

  return {
    bold: selection.hasFormat('bold'),
    italic: selection.hasFormat('italic'),
    strikethrough: selection.hasFormat('strikethrough'),
    bulletList,
    numberList,
  }
}

const iconToolbarButtonBase: Style = {
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  padding: '0.35rem',
  borderRadius: '0.375rem',
  color: 'var(--gray-900)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 0,
  '&:hover': {
    background: 'var(--gray-100)',
  },
  '&:disabled': {
    opacity: 0.35,
    cursor: 'not-allowed',
  },
}

export const LexicalRichHtmlInputToolbar = () => {
  const [editor] = useLexicalComposerContext()
  const t = useTranslations().app.dashboard.pageContentEditor
  const [formats, setFormats] = useState<ToolbarFormatState>(defaultFormatState)

  const refreshFormats = useCallback(() => {
    editor.getEditorState().read(() => {
      setFormats(readToolbarFormatState())
    })
  }, [editor])

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          refreshFormats()
          return false
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerUpdateListener(() => {
        refreshFormats()
      }),
    )
  }, [editor, refreshFormats])

  const dispatchFormat = useCallback(
    (format: TextFormatType) => {
      void editor.dispatchCommand(FORMAT_TEXT_COMMAND, format)
    },
    [editor],
  )

  const activeTint: Style = {
    color: 'var(--brand-primary)',
    background: 'var(--palette-blue-50)',
  }

  return (
    <div className="c0 jc jd cv d a7 ba u bm je ao jf"
      onMouseDown={(e: MouseEvent) => {
        e.preventDefault()
      }}
    >
      <styled.button
        type="button"
        aria-label={t.richTextBold}
        style={{
          ...iconToolbarButtonBase,
          ...(formats.bold ? activeTint : {}),
        }}
        onClick={() => dispatchFormat('bold')}
      >
        <FontAwesomeIcon
          icon={faBold}
          style={{ width: '1rem', height: '1rem' }}
        />
      </styled.button>
      <styled.button
        type="button"
        aria-label={t.richTextItalic}
        style={{
          ...iconToolbarButtonBase,
          ...(formats.italic ? activeTint : {}),
        }}
        onClick={() => dispatchFormat('italic')}
      >
        <FontAwesomeIcon
          icon={faItalic}
          style={{ width: '1rem', height: '1rem' }}
        />
      </styled.button>
      <styled.button
        type="button"
        aria-label={t.richTextStrikethrough}
        style={{
          ...iconToolbarButtonBase,
          ...(formats.strikethrough ? activeTint : {}),
        }}
        onClick={() => dispatchFormat('strikethrough')}
      >
        <FontAwesomeIcon
          icon={faStrikethrough}
          style={{ width: '1rem', height: '1rem' }}
        />
      </styled.button>
      <styled.button
        type="button"
        aria-label={t.richTextBulletList}
        style={{
          ...iconToolbarButtonBase,
          ...(formats.bulletList ? activeTint : {}),
        }}
        onClick={() => {
          editor.update(() => {
            const f = readToolbarFormatState()
            if (f.bulletList) {
              $removeList()
              return
            }
            if (f.numberList) {
              $removeList()
            }
            $insertList('bullet')
          })
        }}
      >
        <FontAwesomeIcon
          icon={faListUl}
          style={{ width: '1rem', height: '1rem' }}
        />
      </styled.button>
      <styled.button
        type="button"
        aria-label={t.richTextNumberedList}
        style={{
          ...iconToolbarButtonBase,
          ...(formats.numberList ? activeTint : {}),
        }}
        onClick={() => {
          editor.update(() => {
            const f = readToolbarFormatState()
            if (f.numberList) {
              $removeList()
              return
            }
            if (f.bulletList) {
              $removeList()
            }
            $insertList('number')
          })
        }}
      >
        <FontAwesomeIcon
          icon={faListOl}
          style={{ width: '1rem', height: '1rem' }}
        />
      </styled.button>
    </div>
  )
}
